import { axiosInstancev2 } from '../../../lib/axiosInstance';
import React, { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';

const DurationSelectionModal = ({ isOpen, onClose, onProceed, planType = 'PLOT' }) => {
    const [selectedDuration, setSelectedDuration] = useState(3); // Default 3 months
    const [plans, setPlans] = useState([]);

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await axiosInstancev2.get(`/properties/plans?type=${planType}`);
                if (response.data.success) {
                    const formattedPlans = response.data.plans.map(plan => {
                        let label = "";
                        let discount = "";
                        if (plan.duration_months === 3) label = "Quarterly";
                        else if (plan.duration_months === 6) { label = "Half Yearly"; discount = "Save 8%"; }
                        else if (plan.duration_months === 9) { label = "9 Months"; discount = "Save 11%"; }
                        else if (plan.duration_months === 12) { label = "Annual"; discount = "Save 16%"; }
                        else label = `${plan.duration_months} Months`;

                        return {
                            months: plan.duration_months,
                            price: plan.price,
                            label: label,
                            discount: discount
                        };
                    });
                    setPlans(formattedPlans);
                }
            } catch (error) {
                console.error("Error fetching property plans:", error);
            }
        };

        if (isOpen) {
            fetchPlans();
        }
    }, [isOpen, planType]);

    if (!isOpen) return null;

    const handleProceed = () => {
        const plan = plans.find(p => p.months === selectedDuration);
        onProceed(plan);
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all scale-100">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-6 relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                    >
                        <X size={24} />
                    </button>
                    <h2 className="text-2xl font-bold text-white mb-1">Select Plan Duration</h2>
                    <p className="text-blue-100 text-sm">Choose the best plan for your needs</p>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                    {plans.map((plan) => (
                        <div
                            key={plan.months}
                            onClick={() => setSelectedDuration(plan.months)}
                            className={`relative border-2 rounded-xl p-4 cursor-pointer transition-all duration-200 flex justify-between items-center
                                ${selectedDuration === plan.months
                                    ? 'border-blue-600 bg-blue-50'
                                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                }
                            `}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center
                                    ${selectedDuration === plan.months ? 'border-blue-600 bg-blue-600' : 'border-slate-300'}
                                `}>
                                    {selectedDuration === plan.months && <Check size={12} className="text-white" />}
                                </div>
                                <div>
                                    <h3 className="font-semibold text-slate-800">{plan.label} ({plan.months} Months)</h3>
                                    {plan.discount && <span className="text-xs text-green-600 font-medium bg-green-100 px-2 py-0.5 rounded-full">{plan.discount}</span>}
                                </div>
                            </div>
                            <div className="text-right">
                                <span className="block text-lg font-bold text-slate-900">₹{plan.price.toLocaleString()}</span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-100 bg-slate-50">
                    <div className="flex justify-between items-center mb-4 text-slate-700">
                        <span className="font-medium">Total Amount:</span>
                        <span className="text-2xl font-bold text-blue-700">
                            ₹{plans.find(p => p.months === selectedDuration)?.price.toLocaleString()}
                        </span>
                    </div>
                    <button
                        onClick={handleProceed}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-xl font-semibold shadow-lg shadow-blue-500/30 transition-all active:scale-95"
                    >
                        Proceed to Pay
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DurationSelectionModal;
